import React, { useState, useCallback } from 'react';
import { X, Download, ZoomIn, RefreshCw } from 'lucide-react';
import { AttachmentData, refreshAttachmentUrl } from '../../utils/chatApi';

interface ImageMessageProps {
  attachment: AttachmentData;
  isMine: boolean;
}

/**
 * Renders an image attachment in the chat.
 * Includes a lightbox for full-size viewing.
 * Automatically retries with a refreshed URL if the image fails to load (expired presigned URL).
 */
export const ImageMessage: React.FC<ImageMessageProps> = ({ attachment, isMine }) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(attachment.url);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = currentUrl;
    link.download = attachment.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle image load error - try to refresh the presigned URL
  const handleImageError = useCallback(async () => {
    // Only retry once automatically, and only if we have an s3_key
    if (retryCount === 0 && attachment.s3_key) {
      setRetryCount(1);
      setIsRefreshing(true);
      
      try {
        const response = await refreshAttachmentUrl(attachment.s3_key);
        setCurrentUrl(response.url);
        setImageError(false);
        setImageLoaded(false);
      } catch (err) {
        console.error('Failed to refresh attachment URL:', err);
        setImageError(true);
      } finally {
        setIsRefreshing(false);
      }
    } else {
      setImageError(true);
    }
  }, [attachment.s3_key, retryCount]);

  // Manual retry button handler
  const handleManualRetry = async () => {
    if (!attachment.s3_key || isRefreshing) return;
    
    setIsRefreshing(true);
    setImageError(false);
    
    try {
      const response = await refreshAttachmentUrl(attachment.s3_key);
      setCurrentUrl(response.url);
      setImageLoaded(false);
      setRetryCount(0); // Reset retry count for future automatic retries
    } catch (err) {
      console.error('Failed to refresh attachment URL:', err);
      setImageError(true);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isRefreshing) {
    // Show loading state while refreshing URL
    return (
      <div
        className={`
          flex items-center gap-3 px-3 py-2.5 rounded-xl max-w-[280px]
          ${isMine 
            ? 'bg-blue-700 text-white' 
            : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary'
          }
        `}
      >
        <RefreshCw className="w-4 h-4 animate-spin" />
        <div className={`text-sm ${isMine ? 'text-blue-200' : 'text-gray-500'}`}>
          Refreshing image...
        </div>
      </div>
    );
  }

  if (imageError) {
    // Fallback to file card style if image fails to load
    return (
      <div
        className={`
          flex items-center gap-3 px-3 py-2.5 rounded-xl max-w-[280px]
          ${isMine 
            ? 'bg-blue-700 text-white' 
            : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary'
          }
        `}
      >
        <div className="flex-1">
          <div className={`text-sm ${isMine ? 'text-blue-200' : 'text-gray-500'}`}>
            Failed to load image: {attachment.filename}
          </div>
          {attachment.s3_key && (
            <button
              onClick={handleManualRetry}
              className={`text-xs mt-1 underline hover:no-underline ${isMine ? 'text-blue-200' : 'text-blue-500'}`}
            >
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Image Thumbnail */}
      <div
        onClick={() => setIsLightboxOpen(true)}
        className={`
          relative group cursor-pointer rounded-xl overflow-hidden max-w-[280px]
          ${isMine ? 'rounded-br-sm' : 'rounded-bl-sm'}
        `}
      >
        {/* Loading skeleton */}
        {!imageLoaded && (
          <div className="w-[280px] h-[200px] bg-gray-200 dark:bg-dark-bg-tertiary animate-pulse" />
        )}
        
        <img
          src={currentUrl}
          alt={attachment.filename}
          onLoad={() => setImageLoaded(true)}
          onError={handleImageError}
          className={`
            max-w-full max-h-[300px] object-cover transition-opacity
            ${imageLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'}
          `}
        />

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
            <div className="bg-black/60 text-white p-2 rounded-full">
              <ZoomIn className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* File info overlay */}
        <div className={`
          absolute bottom-0 left-0 right-0 px-2 py-1.5 
          bg-gradient-to-t from-black/60 to-transparent
          opacity-0 group-hover:opacity-100 transition-opacity
        `}>
          <p className="text-white text-xs truncate">{attachment.filename}</p>
          <p className="text-white/70 text-xs">{formatFileSize(attachment.size_bytes)}</p>
        </div>
      </div>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center"
          onClick={() => setIsLightboxOpen(false)}
        >
          {/* Close button */}
          <button
            onClick={() => setIsLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Download button */}
          <button
            onClick={handleDownload}
            className="absolute top-4 right-16 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            title="Download"
          >
            <Download className="w-6 h-6" />
          </button>

          {/* Full-size image */}
          <img
            src={currentUrl}
            alt={attachment.filename}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />

          {/* Filename at bottom */}
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <p className="text-white text-sm">{attachment.filename}</p>
            <p className="text-white/60 text-xs">{formatFileSize(attachment.size_bytes)}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageMessage;

